import { Box, LinearProgress } from "@mui/material"
import { type ColumnDef, type Row, getCoreRowModel } from "@tanstack/react-table"
import type { FC } from "react"
import React from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { FULL_LIST_LIMIT } from "@/constants/common"
import type { ProductSourceChangeEvent, ProductSourceChangeRecord } from "@/graphql/codegen/graphql"
import { SourceChangeStatusChip } from "@/screens/Products/components/SourceChanges/components/SourceChangeStatusChip"
import { useGetProductSourceChangeRecords } from "@/screens/Products/components/SourceChanges/hooks/useGetProductSourceChangeRecords"

const SOURCE_CHANGE_ROW_COLUMNS: ColumnDef<ProductSourceChangeRecord>[] = [
  {
    accessorKey: "companyName",
    header: "Customer",
    cell: ({ row }) => row.original.company?.name ?? null,
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <SourceChangeStatusChip
        status={row.original.approvalStatus}
        data-testid={`source-change.history-row-details.status-chip-${row.original.approvalStatus}`}
      />
    ),
    enableSorting: false,
  },
]

interface SourceChangeRowDetailsProps {
  row: Row<ProductSourceChangeEvent>
}

export const SourceChangeRowDetails: FC<SourceChangeRowDetailsProps> = ({ row }) => {
  const { sourceChanges, isFetching } = useGetProductSourceChangeRecords({
    sourceChangeBatchId: row.original.sourceChangeBatchId,
    offset: 0,
    limit: FULL_LIST_LIMIT,
  })

  return (
    <Box
      className='bg-primary-100 p-4'
      data-testid={`source-change.history-row-details.container-${row.original.sourceChangeBatchId}`}
    >
      {isFetching ? (
        <LinearProgress />
      ) : (
        <AerosBaseTable
          features={{
            table: { stickyHeader: false },
            tanstackOptions: {
              data: sourceChanges,
              columns: SOURCE_CHANGE_ROW_COLUMNS,
              getCoreRowModel: getCoreRowModel(),
              getRowId: (sourceChangeRecord) => sourceChangeRecord.id,
            },
          }}
          noDataMessage='No source change rows found'
        />
      )}
    </Box>
  )
}
